/**
 * Family portal service (Phase 12, plan §3A).
 *
 * Guardian reads (JWT, permission portal:read) and the public invite routes
 * (no JWT: skipAuth, like POST /auth/login, so no Authorization header is sent and a
 * 401 never starts the token-refresh loop).
 *
 * SECURITY: never log request bodies (last name, DOB, password, registration token) or
 * response data (PHI). Only error codes may be logged.
 */

import { apiClient } from '../api-client';
import { isApiError } from '../../dtos/common/api.dto';
import type {
  PortalMe,
  PortalHome,
  PortalSchedule,
  PortalSummaries,
  PortalCarePlan,
  PortalMedications,
  PortalGrowth,
  PortalCareTeam,
  PortalDemographics,
  PortalContact,
  PortalGateCode,
} from '../../dtos/portal/portal.dto';
import type {
  VerifyInviteRequest,
  VerifyInviteResponse,
  RegisterInviteRequest,
  RegisterInviteResponse,
  InviteErrorCode,
} from '../../dtos/portal/portalInvite.dto';

const GATE_CODES: ReadonlySet<string> = new Set([
  'PORTAL_PENDING_APPROVAL',
  'PORTAL_ACCESS_ENDED',
  'PORTAL_UNAVAILABLE',
]);

const INVITE_CODES: ReadonlySet<string> = new Set([
  'INVITE_INVALID',
  'INVITE_EXPIRED',
  'INVITE_LOCKED',
  'TURNSTILE_REQUIRED',
  'PORTAL_UNAVAILABLE',
  'REGISTRATION_TOKEN_INVALID',
  'PASSWORD_POLICY',
]);

/** Generic, field-agnostic messages for the public accept page (never say which field was wrong). */
export const INVITE_MESSAGES: Record<InviteErrorCode, string> = {
  INVITE_INVALID: "We couldn't verify those details. Check the link and the information you entered, then try again.",
  INVITE_EXPIRED: 'This invitation has expired. Ask the agency to send a new one.',
  INVITE_LOCKED: 'This invitation is locked after too many attempts. Contact the agency to get a new one.',
  TURNSTILE_REQUIRED: 'Complete the verification below to continue.',
  PORTAL_UNAVAILABLE: 'The family portal is not available for this agency right now. Contact the agency.',
  REGISTRATION_TOKEN_INVALID: 'Your verification timed out. Enter your details again.',
  PASSWORD_POLICY: 'Password does not meet the requirements. Please choose a stronger password.',
  RATE_LIMITED: 'Too many attempts. Please wait a few minutes and try again.',
  UNKNOWN: 'Something went wrong. Please try again.',
};

/** An invite verify/register failure with a stable code and a message safe to show. */
export class PortalInviteError extends Error {
  readonly code: InviteErrorCode;
  readonly status?: number;

  constructor(code: InviteErrorCode, status?: number) {
    super(INVITE_MESSAGES[code]);
    this.name = 'PortalInviteError';
    this.code = code;
    this.status = status;
  }
}

/** Guardian gate code from a thrown /portal/* error, or null for other failures. */
export function portalGateCode(err: unknown): PortalGateCode | null {
  if (!isApiError(err)) return null;
  const code = err.domainCode;
  if (code && GATE_CODES.has(code)) return code as PortalGateCode;
  if (err.statusCode === 403) return 'FORBIDDEN';
  return null;
}

function toInviteError(err: unknown): PortalInviteError {
  if (err instanceof PortalInviteError) return err;
  if (!isApiError(err)) return new PortalInviteError('UNKNOWN');
  const code = err.domainCode;
  if (code && INVITE_CODES.has(code)) return new PortalInviteError(code as InviteErrorCode, err.statusCode);
  if (err.statusCode === 423) return new PortalInviteError('INVITE_LOCKED', 423);
  if (err.statusCode === 429 || code === 'RATE_LIMITED') return new PortalInviteError('RATE_LIMITED', err.statusCode);
  if (err.statusCode === 400) return new PortalInviteError('INVITE_INVALID', 400);
  return new PortalInviteError('UNKNOWN', err.statusCode);
}

const patientPath = (patientId: string, item: string) =>
  `/portal/patients/${encodeURIComponent(patientId)}/${item}`;

export class PortalService {
  /** GET /portal/me: works for pending guardians too. */
  static async getMe(): Promise<PortalMe> {
    const response = await apiClient.get<PortalMe>('/portal/me');
    return response.data;
  }

  static async getHome(patientId: string): Promise<PortalHome> {
    return (await apiClient.get<PortalHome>(patientPath(patientId, 'home'))).data;
  }

  static async getSchedule(patientId: string): Promise<PortalSchedule> {
    return (await apiClient.get<PortalSchedule>(patientPath(patientId, 'schedule'))).data;
  }

  static async getSummaries(patientId: string): Promise<PortalSummaries> {
    return (await apiClient.get<PortalSummaries>(patientPath(patientId, 'summaries'))).data;
  }

  static async getCarePlan(patientId: string): Promise<PortalCarePlan> {
    return (await apiClient.get<PortalCarePlan>(patientPath(patientId, 'careplan'))).data;
  }

  static async getMedications(patientId: string): Promise<PortalMedications> {
    return (await apiClient.get<PortalMedications>(patientPath(patientId, 'medications'))).data;
  }

  static async getGrowth(patientId: string): Promise<PortalGrowth> {
    return (await apiClient.get<PortalGrowth>(patientPath(patientId, 'growth'))).data;
  }

  static async getCareTeam(patientId: string): Promise<PortalCareTeam> {
    return (await apiClient.get<PortalCareTeam>(patientPath(patientId, 'careteam'))).data;
  }

  static async getDemographics(patientId: string): Promise<PortalDemographics> {
    return (await apiClient.get<PortalDemographics>(patientPath(patientId, 'demographics'))).data;
  }

  static async getContact(patientId: string): Promise<PortalContact> {
    return (await apiClient.get<PortalContact>(patientPath(patientId, 'contact'))).data;
  }

  /** POST /portal/invites/verify (public). Throws PortalInviteError. */
  static async verifyInvite(request: VerifyInviteRequest): Promise<VerifyInviteResponse> {
    try {
      const body: VerifyInviteRequest = {
        code: request.code,
        lastName: request.lastName,
        dob: request.dob,
        ...(request.turnstileToken ? { turnstileToken: request.turnstileToken } : {}),
      };
      const response = await apiClient.post<VerifyInviteResponse>('/portal/invites/verify', body, { skipAuth: true });
      return response.data;
    } catch (err) {
      throw toInviteError(err);
    }
  }

  /** POST /portal/invites/register (public). Throws PortalInviteError. */
  static async registerInvite(request: RegisterInviteRequest): Promise<RegisterInviteResponse> {
    try {
      const body: RegisterInviteRequest = request.password
        ? { registrationToken: request.registrationToken, password: request.password }
        : { registrationToken: request.registrationToken };
      const response = await apiClient.post<RegisterInviteResponse>('/portal/invites/register', body, { skipAuth: true });
      return response.data;
    } catch (err) {
      throw toInviteError(err);
    }
  }
}

export default PortalService;
